// ===================== Characters =====================
// The Wanderer — "the main guy" — starts with NO element; the run's level-up
// cards let you adopt one. Other characters begin already attuned and cost
// banked coins to unlock, giving each run a different flavor.
//
// Every character carries ONE signature rule beyond stats — `hooks` the engine
// calls at its existing content-blind seams (onHit after the element's own
// hook, onKill in the kill pipeline, onContact when a foe strikes the player
// in melee), or the `agency` count folded into the run's reroll/banish budget.
// Hooks damage through dealDamage with applyElement:false, the same
// no-recursion rule element riders follow.
import { defineCharacter } from '../core/registries.js';

defineCharacter({
  id: 'wanderer', name: 'The Wanderer', title: 'Elementless',
  sprite: 'player_wanderer', startElement: 'neutral', startWeapon: 'bolt',
  cost: 0,
  desc: 'A blank slate. Adopts any element you find and bends it to raw force. SIGNATURE — Freedom: +1 Reroll and +1 Banish every run.',
  stats: {},
  agency: 1,
});

defineCharacter({
  id: 'pyre', name: 'Pyre', title: 'The Ember',
  sprite: 'player_pyre', startElement: 'fire', startWeapon: 'nova',
  cost: 40,
  desc: 'Born of flame, fragile of frame. SIGNATURE — Cinderheart: a foe felled while burning detonates, searing up to 3 neighbours for 12% of its own vigor.',
  stats: { maxHp: 82, damageMult: 1.25, moveSpeed: 200 },
  hooks: {
    onKill(game, e) {
      if (e.boss || e.burnT <= 0) return;
      const near = game.enemiesInRadius(e.x, e.y, 90);
      let hit = 0;
      for (const o of near) {
        if (o === e || o.dead) continue;
        game.dealDamage(o, e.maxHp * 0.12, { applyElement: false });
        if (++hit >= 3) break;
      }
      if (hit) game.particles_ring(e.x, e.y, 90, '#ff5a2b');
    },
  },
});

defineCharacter({
  id: 'frost', name: 'Frost', title: 'The Bulwark',
  sprite: 'player_frost', startElement: 'ice', startWeapon: 'orbit',
  cost: 60,
  desc: 'A frozen sentinel, slow but sturdy. SIGNATURE — Rimeward: whatever strikes him is chilled to the bone, and lesser foes may freeze outright.',
  stats: { maxHp: 145, moveSpeed: 176, armor: 3 },
  hooks: {
    onContact(game, e) {
      e.slowT = Math.max(e.slowT, 3);
      e.slowMult = Math.min(e.slowMult === 1 ? 0.5 : e.slowMult, 0.5);
      if (!e.boss && game.rng.next() < 0.15) e.freezeT = Math.max(e.freezeT, 1.0);
    },
  },
});

defineCharacter({
  id: 'storm', name: 'Storm', title: 'The Spark',
  sprite: 'player_storm', startElement: 'lightning', startWeapon: 'spark',
  cost: 80,
  desc: 'Fast and frantic. SIGNATURE — Static: every critical hit arcs to a nearby foe for 60% of the blow.',
  stats: { maxHp: 76, moveSpeed: 250, attackSpeedMult: 1.15, critChance: 0.10 },
  hooks: {
    onHit(game, e, dmg, crit) {
      if (!crit) return;
      const near = game.enemiesInRadius(e.x, e.y, 140);
      for (const o of near) {
        if (o === e || o.dead) continue;
        game.dealDamage(o, dmg * 0.6, { applyElement: false });
        game.particles_spark(o.x, o.y, '#ffe14d', 3);
        break;
      }
    },
  },
});

defineCharacter({
  id: 'wisp', name: 'Verdant', title: 'The Blightborn',
  sprite: 'player_wisp', startElement: 'poison', startWeapon: 'boomerang',
  cost: 100,
  desc: 'Patient and toxic. SIGNATURE — Deep Rot: a quarter of his strikes on an infected foe squeeze out a burst of its own venom early.',
  stats: { maxHp: 105, regen: 1.5, xpMult: 1.12 },
  hooks: {
    onHit(game, e) {
      if (e.poisonT <= 0 || !e.poisonStacks) return;
      if (game.rng.next() >= 0.25) return;
      // A quarter-second of the target's own rot, paid out up front.
      game.dealDamage(e, e.poisonDps * e.poisonStacks * 0.25, { applyElement: false });
      game.particles_spark(e.x, e.y, '#7dff5c', 3);
    },
  },
});

defineCharacter({
  id: 'saint', name: 'The Saint', title: 'The Radiant',
  sprite: 'player_saint', startElement: 'holy', startWeapon: 'whip',
  cost: 160,
  desc: 'Blessed and enduring, but strikes softer. SIGNATURE — Aegis: every 50th kill consecrates +1 armor for the run, up to +8.',
  stats: { maxHp: 120, regen: 3, damageMult: 0.92, luck: 1 },
  hooks: {
    onKill(game) {
      if (game.kills % 50 !== 0) return;
      const p = game.player;
      p._aegis = p._aegis || 0;
      if (p._aegis >= 8) return;
      p._aegis++;
      p.armor += 1;
      game.particles_ring(p.x, p.y, 60, '#ffe9a8');
    },
  },
});
